import React, { useState } from 'react';
import { 
  IoMdCheckmarkCircle, 
  IoMdCloseCircle,
  IoMdStar,
  IoMdTrendingUp,
  IoMdTime,
  IoMdChatbubbles,
  IoMdCall,
  IoMdArrowForward,
  IoMdBulb,
  IoMdThumbsUp,
  IoMdWarning
} from 'react-icons/io';

export function RecommendationsTab() {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  // Dummy recommendations data
  const recommendations = {
    all: [
      {
        id: 1,
        type: 'timing',
        priority: 'high',
        title: 'Optimize Response Time',
        description: 'Your average response time is 2.3 minutes. Responding within 1 minute could increase customer satisfaction by 15%.',
        impact: 'High',
        effort: 'Low',
        status: 'pending',
        icon: IoMdTime,
        color: 'blue',
        metrics: { current: '2.3 min', target: '1.0 min', improvement: '57%' }
      },
      {
        id: 2,
        type: 'engagement',
        priority: 'medium',
        title: 'Increase Message Frequency',
        description: 'Customer engagement drops after 3 days of inactivity. Send a follow-up message to re-engage.',
        impact: 'Medium',
        effort: 'Low',
        status: 'pending',
        icon: IoMdChatbubbles,
        color: 'green',
        metrics: { current: '5 days', target: '2 days', improvement: '60%' }
      },
      {
        id: 3,
        type: 'content',
        priority: 'high',
        title: 'Use Personalized Templates',
        description: 'Personalized messages have 3x higher open rates. Use customer name and purchase history in templates.',
        impact: 'High',
        effort: 'Medium',
        status: 'pending',
        icon: IoMdStar,
        color: 'purple',
        metrics: { current: '45%', target: '80%', improvement: '78%' }
      },
      {
        id: 4,
        type: 'timing',
        priority: 'low',
        title: 'Schedule Messages for Peak Hours',
        description: 'Peak activity is between 11 AM - 2 PM. Schedule important messages during this window.',
        impact: 'Medium',
        effort: 'Low',
        status: 'applied',
        icon: IoMdTrendingUp,
        color: 'orange',
        metrics: { current: 'Random', target: 'Peak Hours', improvement: '35%' }
      },
      {
        id: 5,
        type: 'engagement',
        priority: 'high',
        title: 'Enable Auto-Responses',
        description: 'Enable automated responses for common queries to reduce response time and improve customer experience.',
        impact: 'High',
        effort: 'Medium',
        status: 'pending',
        icon: IoMdBulb,
        color: 'blue',
        metrics: { current: 'Manual', target: 'Auto', improvement: '70%' }
      },
      {
        id: 6,
        type: 'content',
        priority: 'medium',
        title: 'Add Rich Media to Messages',
        description: 'Messages with images or videos have 2.5x higher engagement. Consider adding media to your templates.',
        impact: 'Medium',
        effort: 'High',
        status: 'pending',
        icon: IoMdThumbsUp,
        color: 'green',
        metrics: { current: '20%', target: '60%', improvement: '200%' }
      },
      {
        id: 7,
        type: 'engagement',
        priority: 'low',
        title: 'Follow Up on Unanswered Calls',
        description: 'You have 3 unanswered calls from this customer. Send a message to follow up.',
        impact: 'Low',
        effort: 'Low',
        status: 'pending',
        icon: IoMdCall,
        color: 'red',
        metrics: { current: '3 calls', target: '0 calls', improvement: '100%' }
      },
      {
        id: 8,
        type: 'content',
        priority: 'medium',
        title: 'Use Shorter Message Length',
        description: 'Messages under 160 characters have higher read rates. Consider condensing your templates.',
        impact: 'Medium',
        effort: 'Low',
        status: 'applied',
        icon: IoMdWarning,
        color: 'yellow',
        metrics: { current: '280 chars', target: '150 chars', improvement: '46%' }
      }
    ]
  };

  const categories = [
    { id: 'all', label: 'All Recommendations', count: recommendations.all.length },
    { id: 'timing', label: 'Timing', count: recommendations.all.filter(r => r.type === 'timing').length },
    { id: 'engagement', label: 'Engagement', count: recommendations.all.filter(r => r.type === 'engagement').length },
    { id: 'content', label: 'Content', count: recommendations.all.filter(r => r.type === 'content').length },
  ];

  const filteredRecommendations = selectedCategory === 'all' 
    ? recommendations.all 
    : recommendations.all.filter(r => r.type === selectedCategory);

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-700 border-red-300';
      case 'medium': return 'bg-yellow-100 text-yellow-700 border-yellow-300';
      case 'low': return 'bg-blue-100 text-blue-700 border-blue-300';
      default: return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  const getImpactColor = (impact: string) => {
    switch (impact) {
      case 'High': return 'text-red-600 bg-red-50';
      case 'Medium': return 'text-yellow-600 bg-yellow-50';
      case 'Low': return 'text-blue-600 bg-blue-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getEffortColor = (effort: string) => {
    switch (effort) {
      case 'Low': return 'text-green-600 bg-green-50';
      case 'Medium': return 'text-yellow-600 bg-yellow-50';
      case 'High': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const RecommendationCard = ({ recommendation }: { recommendation: any }) => {
    const Icon = recommendation.icon;
    const colorClasses = {
      blue: 'bg-blue-50 text-blue-600',
      green: 'bg-green-50 text-green-600',
      purple: 'bg-purple-50 text-purple-600',
      orange: 'bg-orange-50 text-orange-600',
      red: 'bg-red-50 text-red-600',
      yellow: 'bg-yellow-50 text-yellow-600',
    };

    return (
      <div className={`bg-white rounded-xl border-2 ${
        recommendation.status === 'applied' 
          ? 'border-green-300 bg-green-50/30' 
          : 'border-gray-200'
      } p-6 shadow-sm hover:shadow-md transition-shadow`}>
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-start space-x-4 flex-1">
            <div className={`p-3 rounded-lg ${colorClasses[recommendation.color as keyof typeof colorClasses]}`}>
              <Icon className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <div className="flex items-center space-x-2 mb-2">
                <h3 className="text-lg font-semibold text-gray-900">{recommendation.title}</h3>
                {recommendation.status === 'applied' && (
                  <span className="flex items-center space-x-1 text-xs font-medium text-green-700 bg-green-100 px-2 py-1 rounded-full">
                    <IoMdCheckmarkCircle className="w-3 h-3" />
                    <span>Applied</span>
                  </span>
                )}
              </div>
              <p className="text-gray-600 text-sm mb-4">{recommendation.description}</p>
              
              {/* Metrics */}
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-500 mb-1">Current</div>
                  <div className="text-sm font-semibold text-gray-900">{recommendation.metrics.current}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-500 mb-1">Target</div>
                  <div className="text-sm font-semibold text-gray-900">{recommendation.metrics.target}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-500 mb-1">Improvement</div>
                  <div className="text-sm font-semibold text-green-600">{recommendation.metrics.improvement}</div>
                </div>
              </div>

              {/* Tags */}
              <div className="flex items-center space-x-2 flex-wrap gap-2">
                <span className={`text-xs font-medium px-2 py-1 rounded-full border ${getPriorityColor(recommendation.priority)}`}>
                  {recommendation.priority.toUpperCase()} Priority
                </span>
                <span className={`text-xs font-medium px-2 py-1 rounded-full ${getImpactColor(recommendation.impact)}`}>
                  {recommendation.impact} Impact
                </span>
                <span className={`text-xs font-medium px-2 py-1 rounded-full ${getEffortColor(recommendation.effort)}`}>
                  {recommendation.effort} Effort
                </span>
              </div>
            </div>
          </div>
        </div>
        
        {recommendation.status === 'pending' && (
          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-100">
            <button className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
              Dismiss
            </button>
            <button className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2">
              <span>Apply Recommendation</span>
              <IoMdArrowForward className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex-1 overflow-y-auto bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center space-x-2">
              <IoMdBulb className="w-6 h-6 text-yellow-500" />
              <span>AI Recommendations</span>
            </h2>
            <p className="text-gray-600 mt-1">Personalized suggestions to improve customer engagement and satisfaction</p>
          </div>
          <div className="flex items-center space-x-2 text-sm text-gray-500">
            <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full font-medium">
              {recommendations.all.filter(r => r.status === 'pending').length} New
            </span>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="text-2xl font-bold text-gray-900">{recommendations.all.length}</div>
            <div className="text-sm text-gray-600 mt-1">Total Recommendations</div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="text-2xl font-bold text-red-600">
              {recommendations.all.filter(r => r.priority === 'high').length}
            </div>
            <div className="text-sm text-gray-600 mt-1">High Priority</div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="text-2xl font-bold text-green-600">
              {recommendations.all.filter(r => r.status === 'applied').length}
            </div>
            <div className="text-sm text-gray-600 mt-1">Applied</div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="text-2xl font-bold text-blue-600">
              {recommendations.all.filter(r => r.status === 'pending').length}
            </div>
            <div className="text-sm text-gray-600 mt-1">Pending</div>
          </div>
        </div>

        {/* Category Filter */}
        <div className="flex items-center space-x-2 overflow-x-auto pb-2">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`px-4 py-2 rounded-lg font-medium text-sm whitespace-nowrap transition-colors ${
                selectedCategory === category.id
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
              }`}
            >
              {category.label}
              <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                selectedCategory === category.id
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 text-gray-600'
              }`}>
                {category.count}
              </span>
            </button>
          ))}
        </div>

        {/* Recommendations List */}
        <div className="space-y-4">
          {filteredRecommendations.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
              <IoMdCheckmarkCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Recommendations</h3>
              <p className="text-gray-600">All recommendations have been reviewed for this category.</p>
            </div>
          ) : (
            filteredRecommendations.map((recommendation) => (
              <RecommendationCard key={recommendation.id} recommendation={recommendation} />
            ))
          )}
        </div>

        {/* Quick Actions */}
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button className="bg-white rounded-lg border border-gray-200 p-4 text-left hover:shadow-md transition-shadow">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <IoMdCheckmarkCircle className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <div className="font-semibold text-gray-900">Apply All High Priority</div>
                  <div className="text-xs text-gray-600">Apply {recommendations.all.filter(r => r.priority === 'high' && r.status === 'pending').length} recommendations</div>
                </div>
              </div>
            </button>
            <button className="bg-white rounded-lg border border-gray-200 p-4 text-left hover:shadow-md transition-shadow">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <IoMdTrendingUp className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <div className="font-semibold text-gray-900">View Impact Report</div>
                  <div className="text-xs text-gray-600">See potential improvements</div>
                </div>
              </div>
            </button>
            <button className="bg-white rounded-lg border border-gray-200 p-4 text-left hover:shadow-md transition-shadow">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <IoMdBulb className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <div className="font-semibold text-gray-900">Get More Suggestions</div>
                  <div className="text-xs text-gray-600">Request AI analysis</div>
                </div>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
